import RestartAlt from "@mui/icons-material/RestartAlt";
import TuneOutlined from "@mui/icons-material/TuneOutlined";
import { Alert, Box, Button, Stack, Tab, Tabs, Tooltip } from "@mui/material";
import { mdHeight, xsHeight } from "components/AppBar";
import { ConfirmDialog } from "components/ConfirmDialog";
import { NOTIFICATIONS_LIST_URL } from "components/Notifications/notificationNavigation";
import {
  MyNotificationPreferenceCatalogQuery,
  NotificationPreferenceScope,
  Permission,
  StaffPermission,
} from "generated-graphql/graphql";
import { useAuthorization } from "hooks/useAuthorization";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useTenant } from "hooks/useTenant";
import { useCallback, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";

import { AllStaffTab } from "./AllStaffTab";
import { EncampAlertsTab } from "./EncampAlertsTab";
import { UNMUTE_CONFIRM_BODY, UNMUTE_CONFIRM_HEADER } from "./preferenceProvenance";
import { SaveFooter } from "./PreferenceSaveFooter";
import { usePreferenceActions, usePreferenceCatalogs } from "./preferencesPageState";
import { LoadingState, PreferencesTabPanel } from "./PreferencesPanel";
import { ALL_ORGANIZATIONS, type OrganizationSelection } from "./SubscribedOrganizationList";
import { type NotificationPreferenceDraftController } from "./useNotificationPreferenceDraft";

/**
 * The notification preferences page.
 *
 * Two tabs over the same query: "My preferences" resolves the reader's own view,
 * "Organization defaults" resolves what a member with no preferences of their own
 * inherits. Staff see their own version of "My preferences" in its place: their own
 * settings for one organization or all of them, Encamp-only alerts included; see
 * {@link EncampAlertsTab}. They are separate query invocations rather than one read filtered
 * client-side, because the organization's answer genuinely differs from the
 * reader's wherever the reader has an override — and showing the reader's own rows
 * as the organization's is how an admin ends up saving their personal choices onto
 * everybody.
 *
 * Outside the staff tab, which saves each change as it is made, every switch moves a
 * draft and one Save sends only the entries that moved. Either way only what moved is
 * written, and that is not just tidiness: writing a row for
 * an untouched notification would convert a value that was inheriting into a frozen
 * override, so a later change to an organization default would stop reaching
 * anyone who had ever opened this page.
 */

const RESET_CONFIRM_HEADER = "Reset your notification preferences?";
const RESET_CONFIRM_BODY =
  "Your personal choices will be replaced by your organization's current defaults. This can't be undone.";
const PRESET_CONFIRM_HEADER = "Apply the Standard preset?";
// Says what actually happens: the organization's own overrides are cleared so
// every alert follows Encamp's recommended posture, and keeps following it as
// those recommendations change. Members' own choices are named explicitly because
// an admin's first worry is whether this reaches into everyone's settings.
const PRESET_CONFIRM_BODY =
  "Your organization's own choices will be cleared so every alert follows Encamp's recommended posture, including future updates to it. Members who have set their own preferences keep them. This can't be undone.";

type CatalogData = MyNotificationPreferenceCatalogQuery["myNotificationPreferenceCatalog"];

/**
 * Tab-strip height. Shorter than MUI's 48px default, which left a band of empty
 * chrome above the first widget on a page whose whole job is a dense table.
 */
const TAB_BAR_HEIGHT = 44;

/** The white sub-nav bar the tabs and the scope action share. */
const TAB_BAR_SX = {
  px: 3,
  minHeight: TAB_BAR_HEIGHT,
  backgroundColor: "background.default",
  borderBottom: 1,
  borderColor: "divider",
} as const;

/** Tabs and their labels both take the bar's height, or MUI re-adds its own 48. */
const TABS_SX = {
  minHeight: TAB_BAR_HEIGHT,
  "& .MuiTab-root": { minHeight: TAB_BAR_HEIGHT },
} as const;

/**
 * How wide the content is allowed to get.
 *
 * The three control columns are fixed at 272px plus gaps, so everything past this
 * lands in the name column — which is why an uncapped page put a notification's
 * name and its switches at opposite ends of a wide monitor. With the links-to
 * column gone the fixed side is narrower, so this can be too: it leaves the name
 * column around 530px, enough for a title and a one-line description.
 */
const PANEL_MAX_WIDTH = 840;

/**
 * The staff tab, which stands in for "My preferences": a staff member's own
 * settings, Encamp-only alerts included, for one organization or all of them.
 */
const ENCAMP_TAB = "ENCAMP";

/** The tab that pauses alerts for every staff member, for staff allowed to. */
const ALL_STAFF_TAB = "ALL_STAFF";

type PageTab = NotificationPreferenceScope | typeof ENCAMP_TAB | typeof ALL_STAFF_TAB;

/** URL values for a staff member's tab and organization, so both survive a move between organizations. */
const ORGANIZATION_DEFAULTS_TAB_PARAM = "org";
const ALL_STAFF_TAB_PARAM = "staff";
const ALL_ORGANIZATIONS_PARAM = "all";

/** Every tab; the page offers each only to the callers it is for. */
const TABS = [
  { value: NotificationPreferenceScope.My, label: "My preferences" },
  // The staff version of the tab above, in its place and under its name.
  { value: ENCAMP_TAB, label: "My preferences" },
  { value: NotificationPreferenceScope.OrgDefault, label: "Organization defaults" },
  { value: ALL_STAFF_TAB, label: "All staff" },
] as const;

/**
 * Ids pairing each tab with the panel it controls.
 *
 * Derived from the scope rather than generated, because the panel is a single
 * element that swaps its contents: its `id` and `aria-labelledby` have to change
 * with the selected tab, or a screen reader announces the panel as belonging to
 * whichever tab happened to render first.
 */
function tabDomId(tab: PageTab): string {
  return `notification-preferences-tab-${tab}`;
}

function panelDomId(tab: PageTab): string {
  return `notification-preferences-panel-${tab}`;
}

/**
 * The page's write path: the three mutations, the in-flight flag every control
 * reads, and the handlers behind Save, Reset and the preset.
 *
 * Extracted as a hook because it is all branching — three mutations, each with a
 * refetch that can fail independently of the write that succeeded — and inlining it
 * pushed the component past both the complexity and function-length caps. The
 * component is left holding layout, which is what makes it readable.
 */
/**
 * Refreshes after a write that already succeeded, and says so if the refresh is
 * what failed.
 *
 * Its own hook because it is a distinct failure mode — the change landed, the page
 * did not catch up — and because leaving it inline pushed the actions hook past the
 * function-length cap.
 */
/**
 * The page's two destructive confirmations.
 *
 * Extracted together because they are the same kind of thing and neither reads any
 * of the page's state beyond what is passed here — and because the component that
 * held them had grown past the function-length cap, which is the signal to split.
 */
/**
 * The action beside the tab strip: the preset on the organization tab, the personal
 * reset on the reader's own.
 *
 * One control in one place, because it always acts on the tab it sits next to.
 *
 * Default (primary) colour, never `secondary`: this theme maps the secondary palette
 * to Encamp orange, which is reserved for staff-only surfaces.
 *
 * `describeChild` so the tooltip explains the button rather than renaming it —
 * without it MUI turns the title into an aria-label and the visible words stop being
 * the control's accessible name. The disabled preset button sits inside a span
 * because a disabled child fires no pointer events for the tooltip to hang off.
 */
/**
 * What fills the tab panel: the load failure, the skeletons, or the table and its
 * footer.
 *
 * The error is fatal to the panel and never to the page. Returning only the alert
 * from the page took the tab strip with it, so a failed Organization-defaults query
 * removed the one control that got the reader back to My preferences and left
 * reloading as the only way out. The footer goes inside with it, because it acts on
 * this tab's catalog and there is no loaded catalog to act on.
 */
function PanelBody({
  errorMessage,
  loading,
  catalog,
  controller,
  scope,
  saving,
  mutating,
  scopeAction,
  onSave,
}: {
  errorMessage: string | undefined;
  loading: boolean;
  catalog: CatalogData | undefined;
  controller: NotificationPreferenceDraftController;
  scope: NotificationPreferenceScope;
  saving: boolean;
  mutating: boolean;
  scopeAction: ReactNode;
  onSave: () => Promise<void>;
}) {
  if (errorMessage) {
    return <Alert severity="error">Failed to load notification preferences: {errorMessage}</Alert>;
  }

  return (
    /* The cap lives here so the widget and the action row below it share one edge. */
    <Box sx={{ maxWidth: PANEL_MAX_WIDTH }}>
      {loading && !catalog ? (
        <LoadingState />
      ) : (
        <PreferencesTabPanel
          catalog={catalog}
          controller={controller}
          scope={scope}
          disabled={mutating}
        />
      )}

      <SaveFooter
        dirtyCount={controller.dirtyCount}
        saving={saving}
        mutating={mutating}
        scopeAction={scopeAction}
        onCancel={controller.revert}
        onSave={onSave}
      />
    </Box>
  );
}

function ScopeAction({
  onOrganizationTab,
  presetDisabled,
  mutating,
  onOpenPreset,
  onOpenReset,
}: {
  onOrganizationTab: boolean;
  /** Nothing to apply until the organization catalog has loaded. */
  presetDisabled: boolean;
  mutating: boolean;
  onOpenPreset: () => void;
  onOpenReset: () => void;
}) {
  if (onOrganizationTab) {
    return (
      <Tooltip describeChild title="Set every alert to Encamp's recommended defaults">
        <span>
          <Button
            startIcon={<TuneOutlined />}
            disabled={presetDisabled || mutating}
            onClick={onOpenPreset}
          >
            Apply Standard preset
          </Button>
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip describeChild title="Reset to organization defaults">
      <span>
        <Button startIcon={<RestartAlt />} disabled={mutating} onClick={onOpenReset}>
          Reset preferences
        </Button>
      </span>
    </Tooltip>
  );
}

/**
 * The sub-nav bar: the scope tabs on the left, the action for that scope on the
 * right.
 *
 * White with a divider beneath, the same treatment the facility overview uses, so
 * the strip reads as chrome belonging to the page rather than a widget floating on
 * the canvas. Full-bleed on purpose — the rule under it is what separates chrome
 * from content, and it has to reach both edges to do that.
 *
 * The scope's action used to share this row. It now sits under the widget beside
 * Save and Cancel, where it lines up with the container it acts on rather than with
 * the page edge. A caller with one tab gets no bar: an empty band reads as something
 * that failed to load.
 */
function ScopeTabBar({
  tab,
  tabs,
  onTabChange,
}: {
  tab: PageTab;
  /** The tabs this caller is offered; fewer than two draws no strip at all. */
  tabs: readonly (typeof TABS)[number][];
  onTabChange: (tab: PageTab) => void;
}) {
  if (tabs.length < 2) {
    return null;
  }
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={2}
      sx={TAB_BAR_SX}
    >
      <Tabs
        value={tab}
        onChange={(_event, value: PageTab) => onTabChange(value)}
        // Named, because the page has one other tab-like row of controls and a
        // screen-reader user landing on an unnamed tablist has to guess.
        aria-label="Notification preference scope"
        sx={TABS_SX}
      >
        {tabs.map((offered) => (
          <Tab
            key={offered.value}
            value={offered.value}
            label={offered.label}
            id={tabDomId(offered.value)}
            aria-controls={panelDomId(offered.value)}
          />
        ))}
      </Tabs>
    </Stack>
  );
}

function PreferenceConfirmDialogs({
  resetOpen,
  presetOpen,
  unmuteOpen,
  unmuteScope,
  resetting,
  applyingPreset,
  saving,
  onCloseReset,
  onClosePreset,
  onCloseUnmute,
  onConfirmReset,
  onConfirmPreset,
  onConfirmUnmute,
}: {
  resetOpen: boolean;
  presetOpen: boolean;
  unmuteOpen: boolean;
  unmuteScope: NotificationPreferenceScope;
  resetting: boolean;
  applyingPreset: boolean;
  saving: boolean;
  onCloseReset: () => void;
  onClosePreset: () => void;
  onCloseUnmute: () => void;
  onConfirmReset: () => Promise<void>;
  onConfirmPreset: () => Promise<void>;
  onConfirmUnmute: () => Promise<void>;
}) {
  return (
    <>
      <ConfirmDialog
        open={resetOpen}
        header={RESET_CONFIRM_HEADER}
        info={RESET_CONFIRM_BODY}
        cancelText="Cancel"
        confirmText="Reset preferences"
        loading={resetting}
        onClose={onCloseReset}
        // Returned rather than fired-and-forgotten: the dialog's own loading guard
        // is what stops a second click from resetting twice.
        onConfirm={onConfirmReset}
      />
      <ConfirmDialog
        open={presetOpen}
        header={PRESET_CONFIRM_HEADER}
        info={PRESET_CONFIRM_BODY}
        cancelText="Cancel"
        confirmText="Apply preset"
        loading={applyingPreset}
        onClose={onClosePreset}
        onConfirm={onConfirmPreset}
      />
      <ConfirmDialog
        open={unmuteOpen}
        header={UNMUTE_CONFIRM_HEADER}
        info={UNMUTE_CONFIRM_BODY[unmuteScope]}
        cancelText="Cancel"
        confirmText="Save changes"
        loading={saving}
        onClose={onCloseUnmute}
        onConfirm={onConfirmUnmute}
      />
    </>
  );
}

/**
 * Which tab is open and, on a staff member's My preferences tab, which organization it edits.
 *
 * A staff member's tab and selected organization live in the URL, so a reload keeps
 * them. Picking an organization does not move the page: the staff member stays in
 * the organization they came from and only the panel changes. Their tab defaults to
 * their own preferences. A customer's tab is ordinary page state, as it always was.
 */
/** The query string with one parameter set, or removed for null, and the rest kept. */
function withParam(current: URLSearchParams, key: string, value: string | null): URLSearchParams {
  const next = new URLSearchParams(current);
  if (value === null) {
    next.delete(key);
  } else {
    next.set(key, value);
  }
  return next;
}

/** A staff member's tab from the URL, falling back to their own settings. */
function staffTabFromParam(
  tabParam: string | null,
  canManageOrganizationDefaults: boolean,
  canPauseForAllStaff: boolean
): PageTab {
  if (canManageOrganizationDefaults && tabParam === ORGANIZATION_DEFAULTS_TAB_PARAM) {
    return NotificationPreferenceScope.OrgDefault;
  }
  if (canPauseForAllStaff && tabParam === ALL_STAFF_TAB_PARAM) {
    return ALL_STAFF_TAB;
  }
  return ENCAMP_TAB;
}

const TAB_PARAM_BY_TAB: Partial<Record<PageTab, string>> = {
  [NotificationPreferenceScope.OrgDefault]: ORGANIZATION_DEFAULTS_TAB_PARAM,
  [ALL_STAFF_TAB]: ALL_STAFF_TAB_PARAM,
};

function usePageTab({
  tenantId,
  isStaff,
  canManageOrganizationDefaults,
  canPauseForAllStaff,
  scope,
  setScope,
}: {
  tenantId: string | undefined;
  isStaff: boolean;
  canManageOrganizationDefaults: boolean;
  canPauseForAllStaff: boolean;
  scope: NotificationPreferenceScope;
  setScope: (scope: NotificationPreferenceScope) => void;
}) {
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab: PageTab = isStaff
    ? staffTabFromParam(searchParams.get("tab"), canManageOrganizationDefaults, canPauseForAllStaff)
    : scope;
  const organizationParam = searchParams.get("org");
  let selection: OrganizationSelection = tenantId ?? ALL_ORGANIZATIONS;
  if (organizationParam === ALL_ORGANIZATIONS_PARAM) {
    selection = ALL_ORGANIZATIONS;
  } else if (organizationParam) {
    selection = organizationParam;
  }

  const changeTab = useCallback(
    (tab: PageTab) => {
      if (!isStaff) {
        setScope(
          tab === NotificationPreferenceScope.OrgDefault ? tab : NotificationPreferenceScope.My
        );
        return;
      }
      setSearchParams((current) => withParam(current, "tab", TAB_PARAM_BY_TAB[tab] ?? null));
    },
    [isStaff, setScope, setSearchParams]
  );

  // Picking an organization changes only what the panel edits; the page stays on
  // the organization the staff member is in.
  const selectOrganization = useCallback(
    (next: OrganizationSelection) => {
      let value: string | null = next === ALL_ORGANIZATIONS ? ALL_ORGANIZATIONS_PARAM : next;
      if (next === (tenantId ?? ALL_ORGANIZATIONS)) {
        value = null;
      }
      setSearchParams((current) => withParam(current, "org", value));
    },
    [setSearchParams, tenantId]
  );

  return { activeTab, selection, changeTab, selectOrganization };
}

export function NotificationPreferencesPage({
  staffRoute = false,
}: {
  /** Opened at the staff member's own settings route, beside the inbox. */
  staffRoute?: boolean;
}) {
  const { tenantId, tenant } = useTenant();
  const { hasPermissions } = useAuthorization();
  const { isStaff, hasStaffPermission } = useCurrentUser();

  // The staff route belongs to the inbox, not to an organization.
  useBreadcrumb(
    staffRoute
      ? [{ label: "Notifications", to: NOTIFICATIONS_LIST_URL }, { label: "Settings" }]
      : [
          ...(tenantId ? [{ label: tenant?.name ?? "Unknown Tenant" }] : []),
          { label: "Settings" },
          { label: "Notifications" },
        ]
  );

  // The same bar the write path enforces: setting an organization default is an
  // org-wide act. Offering a tab that could only fail is worse than not offering
  // it, so the tab strip disappears entirely for everyone else.
  const canManageOrganizationDefaults =
    Boolean(tenantId) && hasPermissions([Permission.WriteAllFacility]);
  const canPauseForAllStaff =
    isStaff && hasStaffPermission(StaffPermission.PauseStaffNotifications);

  const [scope, setScope] = useState<NotificationPreferenceScope>(NotificationPreferenceScope.My);
  const { activeTab, selection, changeTab, selectOrganization } = usePageTab({
    tenantId,
    isStaff,
    canManageOrganizationDefaults,
    canPauseForAllStaff,
    scope,
    setScope,
  });
  const onEncampTab = activeTab === ENCAMP_TAB;
  const onAllStaffTab = activeTab === ALL_STAFF_TAB;
  // Staff get their own version of My preferences in its place.
  const offeredTabs = TABS.filter(
    (tab) =>
      (tab.value === NotificationPreferenceScope.My && !isStaff) ||
      (tab.value === NotificationPreferenceScope.OrgDefault && canManageOrganizationDefaults) ||
      (tab.value === ENCAMP_TAB && isStaff) ||
      (tab.value === ALL_STAFF_TAB && canPauseForAllStaff)
  );
  const [resetOpen, setResetOpen] = useState(false);
  const [presetOpen, setPresetOpen] = useState(false);
  // Stable identities, so the write-path handlers are not rebuilt every render.
  const closeResetDialog = useCallback(() => setResetOpen(false), []);
  const closePresetDialog = useCallback(() => setPresetOpen(false), []);

  const {
    myQuery,
    organizationCatalog,
    onOrganizationTab,
    effectiveScope,
    activeCatalog,
    activeController,
    activeQuery,
    refetchBoth,
  } = usePreferenceCatalogs({
    tenantId,
    scope:
      activeTab === NotificationPreferenceScope.OrgDefault
        ? activeTab
        : NotificationPreferenceScope.My,
    canManageOrganizationDefaults,
    skipMine: isStaff,
  });

  const {
    mutating,
    saving,
    resetting,
    applyingPreset,
    handleSave,
    handleReset,
    handleApplyPreset,
    unmuteWarningOpen,
    closeUnmuteWarning,
    confirmSaveAfterWarning,
  } = usePreferenceActions({
    tenantId,
    // The effective scope, never the raw tab state: a save has to write the layer the
    // draft actually came from.
    scope: effectiveScope,
    activeController,
    sections: activeCatalog?.sections,
    channelParentSwitches: activeCatalog?.channelParentSwitches,
    refetchBoth,
    refetchMine: myQuery.refetch,
    closeReset: closeResetDialog,
    closePreset: closePresetDialog,
  });

  // Fatal to the panel, never to the page. Returning only the alert took the tab
  // strip with it, so a failed Organization-defaults query removed the one control
  // that gets the reader back to My preferences and left reloading as the only way
  // out. The action rows go inside the panel with it, because they act on this
  // tab's catalog and there is no loaded catalog to act on.
  const errorMessage = activeQuery.error?.message;

  /**
   * The panel's half of the tab pairing, applied only when the tab strip is
   * actually rendered — a `tabpanel` with no tab above it is a role pointing at
   * nothing.
   */
  const panelTabAria =
    offeredTabs.length > 1
      ? { role: "tabpanel", id: panelDomId(activeTab), "aria-labelledby": tabDomId(activeTab) }
      : {};

  return (
    /*
      Gray canvas with white widgets on it, the same shell the waste and facility
      pages use. The page had no page-level background and no margins, so every
      section ran into the next as one undifferentiated white slab — which read as
      a broken page rather than a dense one.

      No page title: the breadcrumb above already says "Settings / Notifications",
      and the tab strip is what names the two views. A heading here repeated the
      breadcrumb and pushed the first widget below the fold for no information.
    */
    <Box
      sx={{
        backgroundColor: (theme) => theme.palette.canvas,
        /*
          Two parts, because neither alone is enough.

          The `minHeight` keeps a short page looking deliberate rather than a band of
          canvas floating on white. It cannot reach the bottom on its own: this box
          starts below the breadcrumb, so subtracting only the nav leaves it short by
          the breadcrumb's height, and subtracting both would hard-code a second
          element's size into this page.

          The shadow does the filling. It paints a copy of this box's own colour far
          below it, which covers whatever gap is left without affecting layout or
          adding to the scroll area — so the canvas reaches the bottom of any window
          and nothing here has to know what sits above it.
        */
        minHeight: {
          xs: (theme) => `calc(100vh - ${theme.spacing(xsHeight)})`,
          md: (theme) => `calc(100vh - ${theme.spacing(mdHeight)})`,
        },
        boxShadow: (theme) => `0 100vh 0 100vh ${theme.palette.canvas}`,
        clipPath: "inset(0 0 -100vh 0)",
      }}
    >
      {/*
        Tabs and the scope action share one row: the action always acts on the tab
        beside it, so putting them on separate rows made the relationship guesswork
        and wasted a band of vertical space. `minHeight` holds the row steady for a
        caller who gets no tabs at all.
      */}
      {/*
        White bar with a divider beneath, the same sub-nav treatment the facility
        overview uses — the tab strip reads as chrome belonging to the page rather
        than as a widget floating on the canvas. Full-bleed on purpose: the rule
        under it is what separates chrome from content, and it has to reach both
        edges to do that.
      */}
      <ScopeTabBar tab={activeTab} tabs={offeredTabs} onTabChange={changeTab} />

      <Box {...panelTabAria} sx={{ px: 3, pt: 2.5, pb: 4 }}>
        {onAllStaffTab && <AllStaffTab />}
        {onEncampTab && (
          <EncampAlertsTab
            currentTenant={tenant ? { id: tenant.id, name: tenant.name } : undefined}
            selection={selection}
            onSelect={selectOrganization}
          />
        )}
        {!onEncampTab && !onAllStaffTab && (
          <PanelBody
            scopeAction={
              <ScopeAction
                onOrganizationTab={onOrganizationTab}
                presetDisabled={!organizationCatalog}
                mutating={mutating}
                onOpenPreset={() => setPresetOpen(true)}
                onOpenReset={() => setResetOpen(true)}
              />
            }
            errorMessage={errorMessage}
            loading={activeQuery.loading}
            catalog={activeCatalog}
            controller={activeController}
            scope={effectiveScope}
            saving={saving}
            mutating={mutating}
            onSave={handleSave}
          />
        )}
      </Box>

      <PreferenceConfirmDialogs
        resetOpen={resetOpen}
        presetOpen={presetOpen}
        unmuteOpen={unmuteWarningOpen}
        unmuteScope={effectiveScope}
        resetting={resetting}
        applyingPreset={applyingPreset}
        saving={saving}
        onCloseReset={() => setResetOpen(false)}
        onClosePreset={() => setPresetOpen(false)}
        onCloseUnmute={closeUnmuteWarning}
        onConfirmReset={handleReset}
        onConfirmPreset={handleApplyPreset}
        onConfirmUnmute={confirmSaveAfterWarning}
      />
    </Box>
  );
}
